import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import { countries } from '../utils/countries';
import './MyProfile.css';

const MyProfile = () => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Profile form state
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    country: '',
    phone: '',
    currentPassword: ''
  });

  // Password change form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [selectedCountry, setSelectedCountry] = useState(null);

  useEffect(() => {
    // Load user data when component mounts
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || '',
        country: user.country || '',
        phone: user.phone || '',
        currentPassword: ''
      });

      // Set selected country for phone code
      if (user.country) {
        const country = countries.find(c => c.name === user.country);
        setSelectedCountry(country);
      }
    }
  }, [user]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData({
      ...profileData,
      [name]: value
    });

    // Update selected country when country changes
    if (name === 'country') {
      const country = countries.find(c => c.name === value);
      setSelectedCountry(country);
    }
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate current password for local users
    if (user?.authProvider === 'local' && !profileData.currentPassword) {
      setError('Please enter your current password to update your profile');
      return;
    }

    setLoading(true);

    try {
      const response = await authAPI.updateProfile({
        name: profileData.name,
        country: profileData.country,
        phone: profileData.phone,
        currentPassword: profileData.currentPassword
      });

      if (response.data.success) {
        // Update user in context and localStorage
        updateUser(response.data.user);
        setSuccess('Profile updated successfully!');
        // Clear password field
        setProfileData({ ...profileData, currentPassword: '' });
      } else {
        setError(response.data.message || 'Failed to update profile');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate passwords
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }

    setLoading(true);

    try {
      const response = await authAPI.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      if (response.data.success) {
        setSuccess('Password changed successfully!');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const isOAuthUser = user?.authProvider && user.authProvider !== 'local';

  return (
    <div className="profile-page">
      <div className="container">
        <div className="profile-container">
          <h1>My Profile</h1>

          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          {success && (
            <div className="alert alert-success">
              {success}
            </div>
          )}

          {/* Profile Information Section */}
          <div className="profile-section">
            <h2>Profile Information</h2>
            <form onSubmit={handleProfileSubmit}>
              <div className="form-group">
                <label htmlFor="name" className="form-label">
                  Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="form-control"
                  value={profileData.name}
                  onChange={handleProfileChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="form-control"
                  value={profileData.email}
                  disabled
                  style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                />
                <small className="form-text">Email cannot be changed</small>
              </div>

              <div className="form-group">
                <label htmlFor="country" className="form-label">
                  Country
                </label>
                <select
                  id="country"
                  name="country"
                  className="form-control"
                  value={profileData.country}
                  onChange={handleProfileChange}
                >
                  <option value="">Select a country</option>
                  {countries.map((country) => (
                    <option key={country.name} value={country.name}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="phone" className="form-label">
                  Phone Number
                </label>
                <div className="phone-input-group">
                  <input
                    type="text"
                    className="form-control country-code"
                    value={selectedCountry?.code || '+1'}
                    disabled
                    style={{ width: '100px', backgroundColor: '#f3f4f6' }}
                  />
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className="form-control"
                    value={profileData.phone}
                    onChange={handleProfileChange}
                    placeholder="Enter phone number"
                    style={{ flex: 1 }}
                  />
                </div>
                <small className="form-text">
                  Country code is automatically selected based on your country
                </small>
              </div>

              {!isOAuthUser && (
                <div className="form-group">
                  <label htmlFor="currentPasswordProfile" className="form-label">
                    Current Password <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="password"
                    id="currentPasswordProfile"
                    name="currentPassword"
                    className="form-control"
                    value={profileData.currentPassword}
                    onChange={handleProfileChange}
                    required
                    placeholder="Enter your current password to update profile"
                  />
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Update Profile'}
              </button>
            </form>
          </div>

          {/* Change Password Section - Only for local users */}
          {!isOAuthUser && (
            <div className="profile-section">
              <h2>Change Password</h2>
              <form onSubmit={handlePasswordSubmit}>
                <div className="form-group">
                  <label htmlFor="currentPasswordChange" className="form-label">
                    Current Password
                  </label>
                  <input
                    type="password"
                    id="currentPasswordChange"
                    name="currentPassword"
                    className="form-control"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    required
                    placeholder="Enter your current password"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="newPassword" className="form-label">
                    New Password
                  </label>
                  <input
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    className="form-control"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    required
                    minLength="6"
                    placeholder="Enter new password (min 6 characters)"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword" className="form-label">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    className="form-control"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                    minLength="6"
                    placeholder="Confirm new password"
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Changing...' : 'Change Password'}
                </button>
              </form>
            </div>
          )}

          {isOAuthUser && (
            <div className="profile-section">
              <div className="info-box">
                <p>
                  You signed in using {user?.authProvider}. Password management is handled by your {user?.authProvider} account.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyProfile;
